import React from "react";
import "./textoRico.css";

/**
 * Formato minimo para las descripciones, sin librerias.
 *
 * Lo que se guarda es TEXTO PLANO con marcas:
 *   **negrita**   *cursiva*   - item de lista   1. item numerado
 *   linea en blanco = parrafo
 *
 * Las dos clases de lista se distinguen tambien en el guardado. Antes un
 * <ol> se serializaba con "- " igual que un <ul>, asi que una lista
 * numerada volvia convertida en vinetas al recargar el producto.
 *
 * El editor es contentEditable (se ve la negrita mientras se escribe), pero
 * su HTML nunca se guarda: se serializa a este formato recorriendo el DOM y
 * emitiendo solo lo conocido. Todo lo demas (span, style, script, lo que
 * venga pegado desde Word) se cae en la conversion. Y al renderizar se
 * devuelven nodos de React, que escapan el texto solos.
 */

/* ------------------------------------------------------------------ */
/*  Marcas  ->  nodos de React  (para mostrar la descripcion)          */
/* ------------------------------------------------------------------ */

const partirInline = (texto) => {
  const partes = [];
  // ** antes que * para que la negrita gane.
  const re = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*)/g;

  let ultimo = 0;
  let m;

  while ((m = re.exec(texto)) !== null) {
    if (m.index > ultimo) {
      partes.push({ tipo: "texto", valor: texto.slice(ultimo, m.index) });
    }

    const token = m[0];
    if (token.startsWith("**")) {
      partes.push({ tipo: "negrita", valor: token.slice(2, -2) });
    } else {
      partes.push({ tipo: "cursiva", valor: token.slice(1, -1) });
    }

    ultimo = m.index + token.length;
  }

  if (ultimo < texto.length) {
    partes.push({ tipo: "texto", valor: texto.slice(ultimo) });
  }

  return partes;
};

const renderInline = (texto, clave) =>
  partirInline(texto).map((p, i) => {
    if (p.tipo === "negrita") return <strong key={`${clave}-${i}`}>{p.valor}</strong>;
    if (p.tipo === "cursiva") return <em key={`${clave}-${i}`}>{p.valor}</em>;
    return <React.Fragment key={`${clave}-${i}`}>{p.valor}</React.Fragment>;
  });

const enBloques = (valor) => {
  const crudo = String(valor ?? "").replace(/\r\n/g, "\n");
  if (!crudo.trim()) return [];

  const bloques = [];
  let lista = null;        // { ordenada, items }
  let parrafo = [];

  const cerrarParrafo = () => {
    if (parrafo.length) bloques.push({ tipo: "parrafo", lineas: parrafo });
    parrafo = [];
  };

  const cerrarLista = () => {
    if (lista) bloques.push({ tipo: "lista", ...lista });
    lista = null;
  };

  const VINETA = /^\s*[-*]\s+/;
  const NUMERO = /^\s*\d+[.)]\s+/;

  crudo.split("\n").forEach((linea) => {
    const esVineta = VINETA.test(linea);
    const esNumero = !esVineta && NUMERO.test(linea);

    if (esVineta || esNumero) {
      cerrarParrafo();

      // Cambiar de vineta a numero (o al reves) abre una lista nueva. Si no,
      // "- a" seguido de "1. b" saldria como un solo bloque y una de las dos
      // perderia su forma.
      if (lista && lista.ordenada !== esNumero) cerrarLista();
      if (!lista) lista = { ordenada: esNumero, items: [] };

      lista.items.push(linea.replace(esNumero ? NUMERO : VINETA, ""));
      return;
    }

    if (!linea.trim()) {
      cerrarParrafo();
      cerrarLista();
      return;
    }

    cerrarLista();
    parrafo.push(linea);
  });

  cerrarParrafo();
  cerrarLista();

  return bloques;
};

export const renderTextoRico = (valor) => {
  const bloques = enBloques(valor);
  if (!bloques.length) return null;

  return bloques.map((b, i) => {
    if (b.tipo === "lista") {
      const Lista = b.ordenada ? "ol" : "ul";
      return (
        <Lista className="txr-lista" key={`b-${i}`}>
          {b.items.map((item, j) => (
            <li key={`b-${i}-${j}`}>{renderInline(item, `b-${i}-${j}`)}</li>
          ))}
        </Lista>
      );
    }

    return (
      <p className="txr-parrafo" key={`b-${i}`}>
        {b.lineas.map((linea, j) => (
          <React.Fragment key={`b-${i}-${j}`}>
            {j > 0 ? <br /> : null}
            {renderInline(linea, `b-${i}-${j}`)}
          </React.Fragment>
        ))}
      </p>
    );
  });
};

/* ------------------------------------------------------------------ */
/*  Marcas  ->  HTML  (para pintar el contentEditable)                 */
/* ------------------------------------------------------------------ */

const escapar = (t) =>
  String(t)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const inlineAHtml = (texto) =>
  partirInline(texto)
    .map((p) => {
      if (p.tipo === "negrita") return `<strong>${escapar(p.valor)}</strong>`;
      if (p.tipo === "cursiva") return `<em>${escapar(p.valor)}</em>`;
      return escapar(p.valor);
    })
    .join("");

/**
 * Convierte el texto guardado en el HTML que muestra el editor.
 * Se escapa antes de armar el marcado, asi que el texto del usuario nunca
 * puede introducir etiquetas.
 */
export const marcasAHtml = (valor) => {
  const bloques = enBloques(valor);
  if (!bloques.length) return "";

  return bloques
    .map((b) => {
      if (b.tipo === "lista") {
        const items = b.items.map((i) => `<li>${inlineAHtml(i)}</li>`).join("");
        const tag = b.ordenada ? "ol" : "ul";
        return `<${tag}>${items}</${tag}>`;
      }
      return `<p>${b.lineas.map(inlineAHtml).join("<br>")}</p>`;
    })
    .join("");
};

/* ------------------------------------------------------------------ */
/*  DOM del editor  ->  marcas  (lo que se guarda)                     */
/* ------------------------------------------------------------------ */

// Recorre los hijos y devuelve el texto con marcas inline.
const serializarInline = (nodo) => {
  let salida = "";

  nodo.childNodes.forEach((hijo) => {
    if (hijo.nodeType === Node.TEXT_NODE) {
      salida += hijo.nodeValue;
      return;
    }

    if (hijo.nodeType !== Node.ELEMENT_NODE) return;

    const tag = hijo.tagName.toLowerCase();

    if (tag === "br") {
      salida += "\n";
      return;
    }

    const dentro = serializarInline(hijo);
    if (!dentro.trim()) {
      salida += dentro;
      return;
    }

    // Solo estas etiquetas producen marca. El resto (span, font, div
    // anidado, lo que sea) aporta unicamente su texto.
    const conMarca = (marca) => {
      // El espacio pegado al borde va afuera de la marca: "**Fuerte **y"
      // es marcado mal formado y se renderiza raro.
      const izq = dentro.match(/^\s*/)[0];
      const der = dentro.match(/\s*$/)[0];
      return `${izq}${marca}${dentro.trim()}${marca}${der}`;
    };

    if (tag === "b" || tag === "strong") {
      salida += conMarca("**");
    } else if (tag === "i" || tag === "em") {
      salida += conMarca("*");
    } else {
      salida += dentro;
    }
  });

  return salida;
};

/**
 * Serializa el contenido del editor al formato de texto que se guarda.
 * Solo emite parrafos, saltos, listas, negrita y cursiva: cualquier otra
 * cosa que exista en el DOM se pierde a proposito.
 */
export const htmlAMarcas = (raiz) => {
  if (!raiz) return "";

  const bloques = [];

  // execCommand inserta espacios duros (U+00A0) al aplicar formato.
  // Guardados tal cual rompen el corte de linea y se ven como espacios
  // raros al mostrar la descripcion.
  const normalizar = (texto) =>
    String(texto)
      .replace(/\u00A0/g, " ")
      .replace(/[ \t]+$/gm, "");

  const agregarParrafo = (texto) => {
    const limpio = normalizar(texto);
    bloques.push(limpio);
  };

  const emitirLista = (nodo, ordenada) => {
    const items = [...nodo.children]
      .filter((li) => li.tagName.toLowerCase() === "li")
      .map((li) => normalizar(serializarInline(li)).replace(/\n/g, " ").trim())
      .filter(Boolean)
      // La numeracion se escribe de verdad en el texto guardado en vez de
      // dejarla implicita: asi el valor se entiende leyendolo, sin tener
      // que pasarlo por el renderizador.
      .map((texto, i) => (ordenada ? `${i + 1}. ${texto}` : `- ${texto}`));

    if (items.length) bloques.push(items.join("\n"));
  };

  /**
   * Recorre los hijos emitiendo un bloque por cada cosa conocida.
   *
   * Es recursivo porque el navegador no deja las listas siempre al primer
   * nivel: al convertir "1. " en lista dentro de una linea nueva, Chrome
   * produce <div><ol>...</ol></div>. Mirando solo el primer nivel, ese div
   * caia en serializarInline y la lista se guardaba como texto corrido, sin
   * vinetas ni numeros.
   */
  const recorrer = (padre) => {
    padre.childNodes.forEach((nodo) => {
      if (nodo.nodeType === Node.TEXT_NODE) {
        if (nodo.nodeValue.trim()) agregarParrafo(nodo.nodeValue);
        return;
      }

      if (nodo.nodeType !== Node.ELEMENT_NODE) return;

      const tag = nodo.tagName.toLowerCase();

      if (tag === "ul" || tag === "ol") {
        emitirLista(nodo, tag === "ol");
        return;
      }

      if (tag === "br") {
        bloques.push("");
        return;
      }

      // Un div o p que trae una lista adentro no es un parrafo: se baja un
      // nivel para que la lista salga como lista.
      if (nodo.querySelector && nodo.querySelector("ul, ol")) {
        recorrer(nodo);
        return;
      }

      // div y p son los bloques que genera el navegador al apretar Enter.
      agregarParrafo(serializarInline(nodo));
    });
  };

  recorrer(raiz);

  return bloques
    .join("\n\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/^\n+|\n+$/g, "");
};
